import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, retry } from 'rxjs';
import { GsStructComponent, PageComponentType } from '../../models';
import { DataProvider } from '../basic/data.service';

@Injectable({
  providedIn: 'root'
})
export class ComponentProvider extends DataProvider<any> {
  // Properties & Signals
  override endpoint = 'f/components/';

  // Methods
  /**
   * Loads the default render data for a given component type from the backend
   * @param type of the component to get the default render data for
   * @returns the default render data
   */
  get<T, HO>(type: PageComponentType, headers?: HO): Observable<T> {
    const httpHeaders = this.generateHeaders(headers, true);
    return this.http
      .get<T>(this.endpointWithUrl + type.toLowerCase(), {
        headers: httpHeaders,
        // The component settings travel in the headers only, but the SSR transfer cache keys a request by
        // URL and params alone: several components of the same type on one page would all get the render
        // of the first one (CS4-2808). The hash gives every configuration its own cache entry.
        params: { cacheKey: ComponentProvider.hashHeaders(httpHeaders) }
      })
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Loads the settings of an component from the backend
   * @param type of the component to get the default settings for
   * @returns the settings of the component
   */
  getDesignerSettings<T>(type: PageComponentType, componentData: GsStructComponent<any>): Observable<T> {
    const sendData = {
      ...componentData
    };
    delete sendData.render;
    return this.http
      .get<T>(this.endpointWithUrl + type.toLowerCase() + '/designer', {
        headers: this.generateHeaders({
          code: sendData
        })
      })
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Builds a short, deterministic hash (FNV-1a, 32 bit) over all headers of a request, so server and
   * browser derive the same value for the same component configuration.
   * @param headers of the request
   * @returns the hash in base 36
   */
  static hashHeaders(headers: HttpHeaders): string {
    const serialized = headers
      .keys()
      .sort()
      .map((key) => `${key}:${headers.getAll(key)?.join(',') ?? ''}`)
      .join('\n');
    let hash = 0x811c9dc5;
    for (let i = 0; i < serialized.length; i++) {
      hash ^= serialized.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
  }
}
